import {
  containersControllerGetPartnerContainers,
  productsControllerCreateProductWithSelections,
  productsControllerGetProduct,
  productsControllerGetProductBillOfOperations,
  productsControllerListProducts,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PartnerContainer,
  ProductBillOfOperations,
  ProductDetail,
  ProductListItem,
} from './types';

export const PRODUCTS_KEY = 'products' as const;

// ─── Product list ─────────────────────────────────────────────────

export function productListQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: [PRODUCTS_KEY, 'list', partnerId] as const,
    queryFn: async ({ signal }) => {
      const res = await productsControllerListProducts({ partner_id: partnerId ?? '' }, { signal });
      return res.data as ProductListItem[];
    },
    enabled: !!partnerId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Product detail ───────────────────────────────────────────────

export function productDetailQueryOptions(productId: string | undefined) {
  return queryOptions({
    queryKey: [PRODUCTS_KEY, 'detail', productId] as const,
    queryFn: async ({ signal }) => {
      if (!productId) throw new Error('productId is required');
      const res = await productsControllerGetProduct(productId, { signal });
      return res.data as ProductDetail;
    },
    enabled: !!productId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Product Bill of Operations ───────────────────────────────────

export function productBillOfOperationsQueryOptions(
  productId: string | undefined,
  version?: number,
) {
  return queryOptions({
    queryKey: [PRODUCTS_KEY, 'bill-of-operations', productId, version ?? 'current'] as const,
    queryFn: async ({ signal }) => {
      if (!productId) throw new Error('productId is required');
      const res = await productsControllerGetProductBillOfOperations(
        productId,
        version === undefined ? {} : { version },
        { signal },
      );
      return res.data as ProductBillOfOperations | null;
    },
    enabled: !!productId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Partner containers (for product creation form) ───────────────

// Returns the partner's *active* containers. The backend filters out rows with
// is_active=false by default; the container config page uses
// `allPartnerContainersQueryOptions` below to opt in to inactive rows.
export function partnerContainersQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: [PRODUCTS_KEY, 'partner-containers', partnerId] as const,
    queryFn: async ({ signal }) => {
      const res = await containersControllerGetPartnerContainers(
        { partner_id: partnerId ?? '' },
        { signal },
      );
      return res.data as PartnerContainer[];
    },
    enabled: !!partnerId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

// Returns *all* partner containers (active + inactive). Only used by the
// `/products/containers` admin config page so it can render deactivated rows
// in the inactive section. Cache key extends the active-query prefix so the
// partial-key invalidation in the toggle mutation refetches both.
export function allPartnerContainersQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: [PRODUCTS_KEY, 'partner-containers', partnerId, 'with-inactive'] as const,
    queryFn: async ({ signal }) => {
      const res = await containersControllerGetPartnerContainers(
        { partner_id: partnerId ?? '', include_inactive: 'true' },
        { signal },
      );
      return res.data as PartnerContainer[];
    },
    enabled: !!partnerId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Create product mutation ──────────────────────────────────────

interface CreateProductParams {
  partner_name: string;
  product_name: string;
  product_description?: string | null;
  container_selections: {
    container_id: string;
    container_option_id: string;
  }[];
}

export function useCreateProduct(partnerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateProductParams) => {
      const res = await productsControllerCreateProductWithSelections(params);
      return res.data as ProductDetail;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_KEY, 'list', partnerId],
      });
    },
  });
}
